/**
 * ShareButton — popover with Copy / WhatsApp / Email actions.
 * Calls /api/marketplace/share-link to build sharable payloads.
 */
import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Mail, MessageCircle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

export default function ShareButton({ kind, target_id, custom_message, disabled, label = "Share", testid }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    if (link || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/marketplace/share-link", { kind, target_id, custom_message });
      setLink(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not build share link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Popover onOpenChange={(o) => { if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="rounded-full" disabled={disabled} data-testid={testid || `share-${kind}`}>
          <Share2 className="h-3.5 w-3.5 mr-1.5" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3 rounded-2xl">
        {loading || !link ? (
          <div className="py-6 text-center">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{link.title}</p>
            <ShareRow icon={Copy}          label={copied ? "Copied!" : "Copy link"}          onClick={copy} testid="share-copy" />
            <ShareRow icon={MessageCircle} label="Share on WhatsApp" href={link.whatsapp_url} testid="share-whatsapp" />
            <ShareRow icon={Mail}          label="Share via email"   href={link.mailto_url}   testid="share-email" />
            <p className="text-[10px] text-muted-foreground break-all pt-1 border-t border-border">{link.url}</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ShareRow({ icon: Icon, label, href, onClick, testid }) {
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm hover:bg-muted transition" data-testid={testid}>
        <Icon className="h-4 w-4" /> {label}
      </a>
    );
  }
  return (
    <button onClick={onClick} className="w-full text-left flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm hover:bg-muted transition" data-testid={testid}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
